import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import NoRecordsFound from 'components/global/NoRecordsFound';
import SubformHeader from 'components/global/SubformHeader';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useDebounce from 'hooks/useDebounce';

import IntegrationDynamicForm from 'pages/OtherIntegrations/Integrations/Modals/IntegrationDynamicForm';
import { StoresSkeleton } from 'pages/SalesChannels/OnlineStores/OnlineStores';
import { getSMSProviders } from 'pages/Settings/SMS/SMSProviders/smsProvidersSlice';

import PATHS from 'utils/paths';

import { faAngleRight } from '@fortawesome/pro-light-svg-icons';

import * as S from './SMSProviders.styled';

const Providers = () => {
	const smsProviders = useSelector((state) => state?.smsProviders.smsProviders);
	const { showInfo } = useAlert();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const isAuthorized = useAccessControl();
	const [searchValue, setSearchValue] = useState('');
	const debouncedValue = useDebounce(searchValue, 500);

	const filteredSMSProviders = useMemo(() => {
		if (!debouncedValue) return smsProviders;

		const searchTerm = debouncedValue.toLowerCase();
		return smsProviders?.filter((integration) => {
			const providerName = integration.name.toLowerCase();
			const translatedName = t(integration.name.toLowerCase());

			return (
				providerName.includes(searchTerm) ||
				providerName.split(' ').join('').includes(searchTerm) ||
				translatedName.includes(searchTerm)
			);
		});
	}, [debouncedValue, smsProviders]);

	const handleCardClick = (provider) => {
		if (!isAuthorized('SMSModule')) {
			showInfo({
				header: 'info.pleaseOpenSMSModuleTitle',
				message: 'info.pleaseOpenSMSModuleMessage'
			});
			navigate(PATHS.OTO_MODULES);
		} else {
			NiceModal.show(IntegrationDynamicForm, {
				fieldNames: provider?.fieldNames,
				logo: provider?.logo,
				content: provider?.content,
				title: provider?.title,
				integrationID: provider?.ID,
				smsConfigID: provider?.smsConfigID
			});
		}
	};

	useEffect(() => {
		dispatch(getSMSProviders());
	}, []);

	return (
		<S.FlexCard>
			<S.HeaderContainer>
				<SubformHeader
					boldTitle="smsProvidersSubtitle"
					lightTitle="smsProvidersDescription"
					className="table-lord-icon"
				/>

				<S.InputContainer>
					<SearchInput
						setValue={setSearchValue}
						value={searchValue}
						placeholder="searchSmsProviders"
						fullWidth
					/>
				</S.InputContainer>
			</S.HeaderContainer>

			{smsProviders?.length ? (
				filteredSMSProviders?.length === 0 ? (
					<NoRecordsFound />
				) : (
					<S.StoresContainer>
						{filteredSMSProviders?.map((provider) => (
							<S.StoreCard key={provider.ID}>
								<S.StyledImage url={provider?.logo} />
								<S.Content>
									<Typography noWrap fontWeight="500" variant="p2">
										{provider?.name}
									</Typography>
									<Typography variant="caption" maxlines={3} noWrap>
										{provider?.description || 'defaultSalesChannelsDescription'}
									</Typography>
									<S.Actions>
										<Button
											variant="outline"
											size="small"
											onClick={() => handleCardClick(provider)}
											fullWidth
											label="connect"
										/>
									</S.Actions>
								</S.Content>
								<S.MobileActions>
									<S.StyledIcon icon={faAngleRight} size="large" />
								</S.MobileActions>
							</S.StoreCard>
						))}
					</S.StoresContainer>
				)
			) : (
				<StoresSkeleton />
			)}
		</S.FlexCard>
	);
};

export default Providers;
